import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthContext';
import { FeedbackProvider } from '@/components/Feedback';
import { RouteGuard, PublicOnly } from '@/auth/RouteGuard';
import { LoginPage } from '@/auth/LoginPage';
import { ResetPassword } from '@/auth/ResetPassword';
import { SetupWizard } from '@/auth/SetupWizard';
import { ATTENDANCE_LOG_PERMISSIONS, DISPATCH_UI_PERMISSIONS } from '@/pages/navItems';

import { DispatchDashboard } from '@/pages/dispatcher/DispatchDashboard';
import { TodayView } from '@/pages/dispatcher/TodayView';
import { TripDetail } from '@/pages/dispatcher/TripDetail';
import { BookingForm } from '@/pages/dispatcher/BookingForm';
import { BulkBookingUpload } from '@/pages/dispatcher/BulkBookingUpload';
import { ApprovalQueue } from '@/pages/dispatcher/ApprovalQueue';
import { LiveMap } from '@/pages/dispatcher/LiveMap';
import { AttendanceLog } from '@/pages/dispatcher/AttendanceLog';
import { CrewAppInstall } from '@/pages/CrewAppInstall';
import { AdminCrewApp } from '@/pages/admin/AdminCrewApp';

import { CrewCurrentTrip } from '@/pages/crew/CrewCurrentTrip';
import { CrewScan } from '@/pages/crew/CrewScan';
import { CrewPod } from '@/pages/crew/CrewPod';
import { CrewCosts } from '@/pages/crew/CrewCosts';
import { CrewEarnings } from '@/pages/crew/CrewEarnings';

import { PortalTrips } from '@/pages/portal/PortalTrips';
import { PortalDocuments } from '@/pages/portal/PortalDocuments';
import { PortalBook } from '@/pages/portal/PortalBook';
import { PortalTripDetail } from '@/pages/portal/PortalTripDetail';
import { PortalUsers } from '@/pages/portal/PortalUsers';

import { AdminHome } from '@/pages/admin/AdminHome';
import { AdminSettings } from '@/pages/admin/AdminSettings';
import { AdminZones } from '@/pages/admin/AdminZones';
import { AdminVehicles } from '@/pages/admin/AdminVehicles';
import { AdminPeople } from '@/pages/admin/AdminPeople';
import { AdminClients } from '@/pages/admin/AdminClients';
import { AdminAddresses } from '@/pages/admin/AdminAddresses';
import { AdminAudit } from '@/pages/admin/AdminAudit';
import { AdminPartners } from '@/pages/admin/AdminPartners';
import { AdminRateCards } from '@/pages/admin/AdminRateCards';
import { AdminRateCardDetail } from '@/pages/admin/AdminRateCardDetail';
import { AdminPayCards } from '@/pages/admin/AdminPayCards';
import { AdminPayCardDetail } from '@/pages/admin/AdminPayCardDetail';
import { AdminBilling } from '@/pages/admin/AdminBilling';
import { AdminCosts } from '@/pages/admin/AdminCosts';
import { AdminPayouts } from '@/pages/admin/AdminPayouts';
import { AdminCommissionPlans } from '@/pages/admin/AdminCommissionPlans';
import { AdminCommissionSettlement } from '@/pages/admin/AdminCommissionSettlement';
import { AdminIntegrations } from '@/pages/admin/AdminIntegrations';
import { AdminFuelPrices } from '@/pages/admin/AdminFuelPrices';
import { NotificationPreferences } from '@/pages/NotificationPreferences';
import { ChangePassword } from '@/pages/ChangePassword';

import { SalesDashboard } from '@/pages/sales/SalesDashboard';
import { SalesProspects } from '@/pages/sales/SalesProspects';
import { SalesSubmit } from '@/pages/sales/SalesSubmit';
import { SalesSettlements } from '@/pages/sales/SalesSettlements';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeedbackProvider>
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/setup" element={<PublicOnly><SetupWizard /></PublicOnly>} />
          <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          {/* Public install page for the crew Android app — no login, signed-in users too. */}
          <Route path="/crew-app" element={<CrewAppInstall />} />

          {/* Dispatch — requires trip/dispatch permissions (Finance is excluded) */}
          <Route
            path="/app/dispatch"
            element={
              <RouteGuard allowed="internal" anyPermission={DISPATCH_UI_PERMISSIONS}>
                <DispatchDashboard />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/today"
            element={
              <RouteGuard allowed="internal" anyPermission={DISPATCH_UI_PERMISSIONS}>
                <TodayView />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/live"
            element={
              <RouteGuard allowed="internal" anyPermission={DISPATCH_UI_PERMISSIONS}>
                <LiveMap />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/attendance"
            element={
              <RouteGuard allowed="internal" anyPermission={ATTENDANCE_LOG_PERMISSIONS}>
                <AttendanceLog />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/trips/:id"
            element={
              <RouteGuard allowed="internal" anyPermission={DISPATCH_UI_PERMISSIONS}>
                <TripDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/book"
            element={
              <RouteGuard allowed="internal" anyPermission={['trips.book']}>
                <BookingForm />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/book/bulk"
            element={
              <RouteGuard allowed="internal" anyPermission={['trips.book']}>
                <BulkBookingUpload mode="internal" />
              </RouteGuard>
            }
          />
          <Route
            path="/app/dispatch/approvals"
            element={
              <RouteGuard allowed="internal" anyPermission={['trips.confirm']}>
                <ApprovalQueue />
              </RouteGuard>
            }
          />

          <Route
            path="/app/admin"
            element={
              <RouteGuard allowed="internal" anyPermission={['settings.manage']}>
                <AdminHome />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/settings"
            element={
              <RouteGuard allowed="internal" anyPermission={['settings.manage']}>
                <AdminSettings />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/zones"
            element={
              <RouteGuard allowed="internal" anyPermission={['settings.manage']}>
                <AdminZones />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/vehicles"
            element={
              <RouteGuard allowed="internal" anyPermission={['fleet.manage']}>
                <AdminVehicles />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/people"
            element={
              <RouteGuard
                allowed="internal"
                anyPermission={['users.manage', 'roles.manage', 'fleet.manage', 'trips.dispatch', 'trips.book']}
              >
                <AdminPeople />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/clients"
            element={
              <RouteGuard allowed="internal" anyPermission={['clients.manage']}>
                <AdminClients />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/addresses"
            element={
              <RouteGuard allowed="internal" anyPermission={['clients.manage', 'settings.manage', 'trips.book']}>
                <AdminAddresses />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/partners"
            element={
              <RouteGuard allowed="internal" anyPermission={['fleet.manage']}>
                <AdminPartners />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/rate-cards"
            element={
              <RouteGuard allowed="internal" anyPermission={['rates.manage', 'rates.activate']}>
                <AdminRateCards />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/rate-cards/:id"
            element={
              <RouteGuard allowed="internal" anyPermission={['rates.manage', 'rates.activate']}>
                <AdminRateCardDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/pay-cards"
            element={
              <RouteGuard allowed="internal" anyPermission={['pay_cards.manage']}>
                <AdminPayCards />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/pay-cards/:id"
            element={
              <RouteGuard allowed="internal" anyPermission={['pay_cards.manage']}>
                <AdminPayCardDetail />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/billing"
            element={
              <RouteGuard allowed="internal" anyPermission={['billing.run', 'billing.push_xero']}>
                <AdminBilling />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/audit"
            element={
              <RouteGuard allowed="internal" anyPermission={['audit.view']}>
                <AdminAudit />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/costs"
            element={
              <RouteGuard allowed="internal" anyPermission={['margin.view']}>
                <AdminCosts />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/payouts"
            element={
              <RouteGuard allowed="internal" anyPermission={['pay.run']}>
                <AdminPayouts />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/commission-plans"
            element={
              <RouteGuard allowed="internal" anyPermission={['sales.manage_plans']}>
                <AdminCommissionPlans />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/commission-settlement"
            element={
              <RouteGuard allowed="internal" anyPermission={['commission.settle']}>
                <AdminCommissionSettlement />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/integrations"
            element={
              <RouteGuard allowed="internal" anyPermission={['integrations.manage', 'settings.manage']}>
                <AdminIntegrations />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/crew-app"
            element={
              <RouteGuard allowed="internal" anyPermission={['settings.manage']}>
                <AdminCrewApp />
              </RouteGuard>
            }
          />
          <Route
            path="/app/admin/fuel"
            element={
              <RouteGuard allowed="internal" anyPermission={['settings.manage']}>
                <AdminFuelPrices />
              </RouteGuard>
            }
          />

          <Route path="/app/sales" element={<RouteGuard allowed="sales"><SalesDashboard /></RouteGuard>} />
          <Route path="/app/sales/prospects" element={<RouteGuard allowed="sales"><SalesProspects /></RouteGuard>} />
          <Route path="/app/sales/submit" element={<RouteGuard allowed="sales"><SalesSubmit /></RouteGuard>} />
          <Route path="/app/sales/settlements" element={<RouteGuard allowed="sales"><SalesSettlements /></RouteGuard>} />

          {/* Crew mobile routes */}
          <Route path="/crew" element={<RouteGuard allowed="crew"><CrewCurrentTrip /></RouteGuard>} />
          <Route path="/crew/scan" element={<RouteGuard allowed="crew"><CrewScan /></RouteGuard>} />
          <Route path="/crew/pod" element={<RouteGuard allowed="crew"><CrewPod /></RouteGuard>} />
          <Route path="/crew/costs" element={<RouteGuard allowed="crew"><CrewCosts /></RouteGuard>} />
          <Route path="/crew/earnings" element={<RouteGuard allowed="crew"><CrewEarnings /></RouteGuard>} />

          {/* Client portal routes */}
          <Route path="/portal" element={<RouteGuard allowed="client" anyPermission={['portal.view_trips']}><PortalTrips /></RouteGuard>} />
          <Route path="/portal/book" element={<RouteGuard allowed="client" anyPermission={['portal.book']}><PortalBook /></RouteGuard>} />
          <Route path="/portal/book/bulk" element={<RouteGuard allowed="client" anyPermission={['portal.book']}><BulkBookingUpload mode="portal" /></RouteGuard>} />
          <Route path="/portal/users" element={<RouteGuard allowed="client" anyPermission={['portal.manage_users']}><PortalUsers /></RouteGuard>} />
          <Route path="/portal/documents" element={<RouteGuard allowed="client" anyPermission={['portal.download_docs']}><PortalDocuments /></RouteGuard>} />
          <Route path="/portal/trips/:id" element={<RouteGuard allowed="client" anyPermission={['portal.view_trips']}><PortalTripDetail /></RouteGuard>} />

          {/* Notification preferences — any authenticated user, chrome adapts to their role */}
          <Route
            path="/notifications"
            element={
              <RouteGuard allowed="any">
                <NotificationPreferences />
              </RouteGuard>
            }
          />

          <Route
            path="/account/password"
            element={
              <RouteGuard allowed="any">
                <ChangePassword />
              </RouteGuard>
            }
          />

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </FeedbackProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
